<template>
  <div class="table-responsive">
    <table class="table table-vcenter">
      <thead>
        <tr>
          <th class="first-row">{{ $t('settings.company.orders.job_title') }}</th>
          <th>Status</th>
          <th>Budget</th>
          <th class="text-end last-row">{{ $t('settings.company.orders.created_at') }}</th>
        </tr>
      </thead>
      <RemoteListView
        endpoint="/v1/account/orders"
        :search-query="data.searchQuery"
        :order="data.sortKey"
        :order-dir="data.sortDir"
        :page-size="100"
        :parameters="data.requestParameters"
        :transformData="groupOrdersByCreated"
        element-type="tbody">
        <template #collection="{ collection }">
          <tr v-for="item in collection" :key="item.id">
            <th class="date-separator" v-if="item.separator" colspan="100%">{{ item.separator }}</th>
            <template v-else>
              <td class="first-row">
                <div class="d-flex flex-column">
                  <h4
                    @click="router.push(getOrderRoute(item as Order, selfServiceEnabled))"
                    role="button"
                    class="text-truncate w-fit">
                    {{ item.title }}
                  </h4>
                  <p class="d-flex gap-1 m-0">
                    <small class="text-muted">#{{ item.id }}</small>
                    <small class="text-muted">&#x2022;</small>
                    <small class="text-muted text-truncate" v-if="item.provider_job_id"
                      >P.{{ item.provider_job_id }}</small
                    >
                  </p>
                </div>
              </td>
              <td>
                <StatusIndicator :status="item.is_draft ? 'draft' : item.lead_order_status" :outlined="true" />
              </td>
              <td class="fw-bold w-full">
                {{ formatCurrency(item.budget_gross_total ?? 0, item.budget_currency) }}
              </td>
              <td class="text-end last-row">
                <div class="d-flex gap-1 flex-column">
                  <p class="m-0 text-nowrap" v-tooltip="`${formatDateTime(item.created_at)}`">
                    {{
                      formatDistanceToNow(new Date(item.created_at || ''), {
                        addSuffix: true,
                      })
                    }}
                  </p>
                  <p class="d-flex align-items-center justify-content-end gap-2 m-0">
                    <span class="avatar avatar-sm">{{ getUserAcronym(getCreatorName(item.creator)) }}</span>
                    <span class="text-muted text-nowrap">{{ getCreatorName(item.creator) }}</span>
                  </p>
                </div>
              </td>
            </template>
          </tr>
        </template>
        <template #footer="{ page, changePage, refresh, startRecordIndex, endRecordIndex, filterCount, pageCount }">
          <tr>
            <td colspan="100%" class="first-row">
              <div class="d-flex align-items-center justify-content-between">
                <p class="m-0 text-muted">
                  <small>
                    {{ startRecordIndex }} - {{ endRecordIndex }} {{ $t('of') }} <strong>{{ filterCount }}</strong>
                    {{ $t('entries') }}
                  </small>
                </p>
                <LiPagination
                  :modelValue="page"
                  @update:modelValue="changePage"
                  :page-count="pageCount"
                  class="m-0 ms-auto"
                  @change="refresh"
                  :labels="{ prev: $t('orders.pagination.prev'), next: $t('orders.pagination.next') }" />
              </div>
            </td>
          </tr>
        </template>
        <template #empty>
          <tr>
            <td colspan="4">
              <div class="empty">
                <div class="empty-icon">
                  <IconMoodEmpty :size="24" :stroke-width="1" />
                </div>
                <p class="empty-title">{{ $t('settings.company.orders.no_orders') }}</p>
                <p class="empty-subtitle text-muted">{{ $t('settings.company.orders.no_orders.hint') }}</p>
              </div>
            </td>
          </tr>
        </template>
      </RemoteListView>
    </table>
  </div>
</template>
<script setup lang="ts">
import translator from '@/locale/translator';
import { computed, reactive } from 'vue';
import RemoteListView from '../RemoteListView.vue';
import { createFilterEQL } from '@/filters/listFilters';
import { differenceInMonths, differenceInYears, formatDistanceToNow, parseISO } from 'date-fns';
import { useRouter } from 'vue-router';
import type { Order } from '@/types/types';
import { getUserAcronym } from '@/func/user';
import { getOrderRoute } from '@/func/orderCreation';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { Pagination as LiPagination } from '@prospective/lithium';
import { IconMoodEmpty } from '@tabler/icons-vue';
import { formatDateTime } from '@/filters/dateFormat';
import { formatCurrency } from '@/locale/format';
import StatusIndicator from '@/components/generic/StatusIndicator.vue';

const router = useRouter();
const featureStore = useCustomerFeaturesStore();
const selfServiceEnabled = computed(() => featureStore.featureOrderManagment?.self_service === true);
const props = defineProps<{ companyId: number }>();
const data = reactive({
  sortKey: 'created_at',
  sortDir: 'desc',
  searchQuery: '',
  requestParameters: {
    EQL: createFilterEQL({ company_id: props.companyId }),
    resolve: 'creator,publications',
  },
});

function groupOrdersByCreated(orders) {
  const now = new Date();
  const grouped: any[] = [];
  const insertedSeparators = new Set();

  const getSeparator = (date: Date) => {
    const monthsDiff = differenceInMonths(now, date);

    if (monthsDiff < 1) return;
    if (monthsDiff < 2) return translator.get('time.last_month');
    if (monthsDiff <= 5) return translator.get('time.more_than_two');
    if (monthsDiff <= 11) return translator.get('time.more_than_six');

    const yearsDiff = differenceInYears(now, date);
    if (yearsDiff < 1) return translator.get('time.more_than_year');

    return date.getFullYear().toString(); // e.g., "2023"
  };

  for (const order of orders) {
    const orderDate = parseISO(order.created_at);
    const separator = getSeparator(orderDate);

    if (separator && !insertedSeparators.has(separator)) {
      grouped.push({ separator });
      insertedSeparators.add(separator);
    }

    grouped.push(order);
  }

  return grouped;
}
function getCreatorName(creator: Order['creator']) {
  if (!creator?.data?.firstname || !creator?.data?.lastname) return creator?.data?.email || translator.get('unknown');
  return `${creator.data.firstname} ${creator.data.lastname}`;
}
</script>
<style scoped>
tr > th {
  color: black !important;
  font-weight: 700;
}

tr > th.date-separator {
  color: black !important;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 6px 20px;
}

tr > td:not(:last-of-type) {
  padding-right: 1.5rem;
}

.first-row {
  padding-left: 20px;
}

.last-row {
  padding-right: 20px;
}
</style>
